import { useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { Card, MomentumBadge } from "./ActionParts";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const BOARD = [["most_viewed", "Most Viewed", "views"], ["most_interested", "Most Interested", "interested"], ["most_attended", "Most Attended", "attended"],
  ["highest_growth", "Highest Growth", "more actions"]];

export const Leaderboards = ({ boards, cats }) => {
  const [cat, setCat] = useState(cats[0]?.name || "");
  const b = boards[cat] || {};
  return (
    <Card title="Category Leaderboards" testId="category-leaderboards">
      <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="leaderboard-category-select" aria-label="Category"
        className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
        {cats.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
      </select>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {BOARD.map(([k, label, unit]) => (
          <div key={k} className="rounded-2xl bg-slate-50 p-4" data-testid={`leader-${k}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</p>
            {b[k] ? <><Link to={`/volunteer/${b[k].id}`} className="mt-2 block font-semibold leading-snug text-slate-900 hover:text-[#1E3A8A]">{b[k].title}</Link>
              <p className="text-xs text-slate-600">{b[k].organization}</p><p className="mt-1 text-sm font-semibold text-emerald-700">{b[k].count} {unit}</p></>
              : <p className="mt-2 text-sm text-slate-600">No activity yet</p>}
          </div>
        ))}
        <div className="rounded-2xl bg-slate-50 p-4" data-testid="leader-most_active_organization">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Most Active Organization</p>
          {b.most_active_organization ? <><p className="mt-2 font-semibold text-slate-900">{b.most_active_organization.organization}</p>
            <p className="mt-1 text-sm font-semibold text-emerald-700">{b.most_active_organization.count} actions</p></> : <p className="mt-2 text-sm text-slate-600">No activity yet</p>}
        </div>
      </div>
    </Card>
  );
};

const OrgList = ({ title, rows, unit, testId }) => (
  <div className="rounded-2xl bg-slate-50 p-4" data-testid={testId}>
    <p className="text-sm font-semibold text-slate-900">{title}</p>
    {rows.length ? <ol className="mt-2 space-y-1.5 text-sm">{rows.map((r, i) => (
      <li key={r.name} className="flex justify-between gap-3"><Link to={`/organizations/${slugify(r.name)}`} className="text-slate-800 hover:text-[#1E3A8A] hover:underline">{i + 1}. {r.name}</Link>
        <span className="shrink-0 font-semibold text-emerald-700">{unit === "%" ? `${r.change_pct > 0 ? "+" : ""}${r.change_pct}%` : `${r.count} ${r.count === 1 ? unit.replace(/s$/, "") : unit}`}</span></li>))}</ol>
      : <p className="mt-2 text-sm text-slate-600">No activity yet</p>}
  </div>
);

export const TopOrganizations = ({ orgs }) => (
  <Card title="Top Organizations" testId="top-organizations">
    <p className="mt-1 text-sm text-slate-600">Organizations, not directories, are ranked. <Link to="/organizations" className="font-semibold text-sky-800 hover:underline">Browse all organizations</Link></p>
    <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      <OrgList title="Top by Interest" rows={orgs.most_interested} unit="interested" testId="orgs-most-interested" />
      <OrgList title="Top by Attendance" rows={orgs.most_attended} unit="attended" testId="orgs-most-attended" />
      <OrgList title="Trending Organizations" rows={orgs.fastest_growing} unit="%" testId="orgs-fastest-growing" />
      <OrgList title="Most Active Organizations" rows={orgs.most_active} unit="actions" testId="orgs-most-active" />
      <OrgList title="Most Opportunities Available" rows={orgs.most_opportunities} unit="open" testId="orgs-most-opportunities" />
      <OrgList title="Most Fundraisers Available" rows={orgs.most_fundraisers} unit="fundraisers" testId="orgs-most-fundraisers" />
      <OrgList title="Most Events Available" rows={orgs.most_events} unit="events" testId="orgs-most-events" />
    </div>
  </Card>
);

export const TrendCharts = ({ trend, momentum, typeMomentum }) => {
  const [view, setView] = useState("actions");
  return (
    <Card title="Participation Trends" testId="trend-charts">
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {[["actions", "Interested vs Attended"], ["type", "By listing type"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} aria-pressed={view === k} data-testid={`trend-view-${k}`}
            className={`rounded-full border px-3 py-1 text-sm ${view === k ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-200 text-slate-700"}`}>{l}</button>
        ))}
        <span className="ml-auto flex flex-wrap items-center gap-2 text-sm text-slate-700">Action Momentum™ <MomentumBadge m={momentum} testId="overall-momentum" /></span>
      </div>
      <div className="mt-4 h-64">
        <ResponsiveContainer>
          {view === "actions" ? (
            <AreaChart data={trend}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="label" fontSize={11} interval={Math.max(0, Math.ceil(trend.length / 8) - 1)} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Legend />
              <Area type="monotone" dataKey="interested" name="Interested" stroke="#1E3A8A" fill="#1E3A8A" fillOpacity={0.15} />
              <Area type="monotone" dataKey="attended" name="Attended" stroke="#2E7D32" fill="#2E7D32" fillOpacity={0.2} /></AreaChart>
          ) : (
            <BarChart data={trend}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="label" fontSize={11} interval={Math.max(0, Math.ceil(trend.length / 8) - 1)} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Legend />
              <Bar dataKey="volunteer" name="Volunteer" stackId="t" fill="#1E3A8A" /><Bar dataKey="event" name="Events" stackId="t" fill="#0F766E" />
              <Bar dataKey="fundraiser" name="Fundraisers" stackId="t" fill="#2E7D32" /></BarChart>
          )}
        </ResponsiveContainer>
      </div>
      <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-700">
        {[["volunteer", "Volunteer participation"], ["event", "Event participation"], ["fundraiser", "Fundraiser participation"]].map(([k, l]) => (
          <span key={k} className="flex items-center gap-2" data-testid={`type-momentum-${slug(k)}`}>{l}: <b>{typeMomentum[k].current}</b><MomentumBadge m={typeMomentum[k]} /></span>
        ))}
      </div>
    </Card>
  );
};
